import React, { useEffect, useCallback, useState } from "react";
import { Button, Input, message } from "antd";
import { useMutationLogin } from '../../../hooks/useAuthen';
import { useForgotPassword, MIN_PASSWORD_LENGTH } from '../../../hooks/useAccountSecurity';
import { useNavigate } from 'react-router-dom';
import LoginForm from '../components/Form';

const Login = () => {
  // ต้องใช้ mutateAsync — mutate เป็น fire-and-forget, await ไม่รอจริง
  const { mutateAsync: fetchLogin, isLoading: isLoadingLogin } = useMutationLogin();
  const navigate = useNavigate();
  const [path,setPath] = useState()
  // ขั้นตอน 2FA (เฉพาะ Panel(Output)) — 'form' → 'setup' (สแกน QR) หรือ 'token' (กรอกรหัส)
  const [step, setStep] = useState('form');
  const [creds, setCreds] = useState(null);   // เก็บ user/pass ไว้ยิงซ้ำพร้อมรหัส
  const [qr, setQr] = useState(null);
  const [totpToken, setTotpToken] = useState('');
  // ลืมรหัสผ่าน — กู้เองด้วยรหัส 6 หลักจาก Google Authenticator ไม่ต้องรอ admin
  const { mutateAsync: forgotPassword, isLoading: isResetting } = useForgotPassword();
  const [forgotForm, setForgotForm] = useState({ username: '', totpToken: '', newPassword: '', confirmPassword: '' });
  const setForgotField = (key) => (value) => setForgotForm((prev) => ({ ...prev, [key]: value }));

  // Redirect if token exists
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const id = sessionStorage.getItem('id');
    
    if (token && id && id != 0) {
      navigate('/payments');
    }
  }, [navigate]);

  // ยิง login — ใช้ทั้งรอบแรก (user/pass) และรอบยืนยันรหัส 6 หลัก
  const doLogin = async (payload) => {
    const result = await fetchLogin(payload);

    // ยังไม่เคยผูก → โชว์ QR ให้สแกน
    if (result?.needTotpSetup) {
      setCreds(payload);
      setQr({ qrDataUrl: result.qrDataUrl, secret: result.secret });
      setTotpToken('');
      setStep('setup');
      return;
    }
    // ผูกแล้ว → ขอรหัส 6 หลัก
    if (result?.needTotpToken) {
      setCreds(payload);
      setTotpToken('');
      setStep('token');
      return;
    }

    // ไม่ต้อง navigate ซ้ำตรงนี้ — useMutationLogin พาไปตามสิทธิ์แล้ว
    // (role 1 → /backoffice, ที่เหลือ → /workspace)
    //
    // เดิมมี navigate('/employees') ค้างอยู่ แต่ไม่เคยทำงานเพราะใช้ mutate ซึ่งไม่รอผล
    // ทำให้ token ยังไม่ถูกเขียนตอนเช็ค พอเปลี่ยนเป็น mutateAsync (เพื่อรับผล 2FA)
    // โค้ดบรรทัดนั้นเลยเริ่มทำงาน แล้วพาไป /employees ซึ่งไม่มีใน route
    // → หลุดออกนอก /backoffice → checkHeaders เห็นว่าไม่มี companyId → เตะออก
  };

  const onSubmitTotp = async () => {
    if (!/^\d{6}$/.test(totpToken)) {
      message.warning('กรอกรหัส 6 หลักจากแอป Google Authenticator');
      return;
    }
    try {
      await doLogin({ ...creds, totpToken });
    } catch (error) {
      message.error(error?.response?.data?.message || 'รหัสไม่ถูกต้อง');
    }
  };

  const onFinish = (async (values) => {
    try {
      await doLogin(values);
      //sessionStorage.setItem('token','xxxx')
      //sessionStorage.setItem('role','1')
      //setPath("/")
      // window.location.href = '/'
      //navigate('/');
    } catch (error) {
      const errorMessage = error.response.data.message || error.message;
      message.error(errorMessage);
    }
  });


  // เดิมพาไป /forget-password ซึ่งไม่มี route อยู่จริง → จอว่าง
  // ทำเป็นขั้นตอนในหน้านี้แทน จะได้ไม่ต้องสลับหน้าไปมา
  const handleForgotPassword = useCallback(() => {
    setForgotForm({ username: '', totpToken: '', newPassword: '', confirmPassword: '' });
    setStep('forgot');
  }, []);

  const backToLogin = useCallback(() => {
    setStep('form');
    setCreds(null);
    setQr(null);
    setTotpToken('');
  }, []);

  const onSubmitForgot = async () => {
    const { username, totpToken: code, newPassword, confirmPassword } = forgotForm;
    if (!username.trim()) {
      message.warning('กรอกชื่อผู้ใช้');
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      message.warning('กรอกรหัส 6 หลักจากแอป Google Authenticator');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      message.warning(`รหัสผ่านต้องยาวอย่างน้อย ${MIN_PASSWORD_LENGTH} ตัวอักษร`);
      return;
    }
    if (newPassword !== confirmPassword) {
      message.warning('รหัสผ่านทั้งสองช่องไม่ตรงกัน');
      return;
    }
    try {
      await forgotPassword({ username: username.trim(), totpToken: code, newPassword });
      message.success('ตั้งรหัสผ่านใหม่สำเร็จ กรุณาเข้าสู่ระบบด้วยรหัสใหม่');
      backToLogin();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || 'ตั้งรหัสผ่านใหม่ไม่สำเร็จ');
    }
  };

  return (
    <div className="bg-gray-50">
      <div className="min-h-screen flex flex-col items-center justify-center py-6 px-4">
        <div className="max-w-md w-full">
          {/* Logo Section */}
          <div className="flex justify-center items-center mr-4 mb-10">
            {/* <a href="#">
              <img 
                alt="LOGO" 
                className="w-[230px]" 
              />
            </a> */}
          </div>
          
          {/* Login Text Section */}
          <div className="flex justify-center items-center mb-4">
            <div className="text-center">
              <p className="text-xl">เข้าสู่ระบบบัญชีของคุณ</p>
              <p className="text-gray-500 text-lg">ยินดีต้อนรับกลับ กรุณากรอกรายละเอียดของคุณ</p>
            </div>
          </div>
          
          {/* Login Form */}
          <div className="p-8 rounded-2xl bg-white shadow">
            {step === 'form' && (
              <LoginForm
                onFinish={onFinish}
                isLoadingLogin={isLoadingLogin}
                handleForgotPassword={handleForgotPassword}
              />
            )}

            {(step === 'setup' || step === 'token') && (
              <div className="text-center">
                {step === 'setup' && (
                  <>
                    <p className="text-lg font-semibold mb-1">ตั้งค่า Google Authenticator</p>
                    <p className="text-gray-500 text-sm mb-4">
                      เปิดแอป Google Authenticator แล้วสแกน QR นี้ (ทำครั้งเดียว)
                    </p>
                    {qr?.qrDataUrl && (
                      <img
                        src={qr.qrDataUrl}
                        alt="QR สำหรับ Google Authenticator"
                        className="mx-auto mb-3"
                        style={{ width: 200, height: 200 }}
                      />
                    )}
                    {qr?.secret && (
                      <p className="text-xs text-gray-400 mb-4 break-all">
                        สแกนไม่ได้? กรอกรหัสนี้เอง: <b>{qr.secret}</b>
                      </p>
                    )}
                  </>
                )}

                {step === 'token' && (
                  <>
                    <p className="text-lg font-semibold mb-1">ยืนยันตัวตน</p>
                    <p className="text-gray-500 text-sm mb-4">
                      กรอกรหัส 6 หลักจากแอป Google Authenticator
                    </p>
                  </>
                )}

                <Input
                  value={totpToken}
                  onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onPressEnter={onSubmitTotp}
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  inputMode="numeric"
                  style={{ textAlign: 'center', fontSize: 26, letterSpacing: 8, marginBottom: 12 }}
                />
                <Button
                  type="primary"
                  block
                  size="large"
                  loading={isLoadingLogin}
                  disabled={isLoadingLogin || totpToken.length !== 6}
                  onClick={onSubmitTotp}
                >
                  ยืนยัน
                </Button>
                <Button
                  type="link"
                  className="mt-2"
                  onClick={backToLogin}
                >
                  ย้อนกลับ
                </Button>
              </div>
            )}

            {step === 'forgot' && (
              <div>
                <p className="text-lg font-semibold mb-1 text-center">ตั้งรหัสผ่านใหม่</p>
                <p className="text-gray-500 text-sm mb-4 text-center">
                  ยืนยันตัวตนด้วยรหัส 6 หลักจากแอป Google Authenticator ของคุณ
                </p>

                <div className="flex flex-col gap-3">
                  <Input
                    value={forgotForm.username}
                    onChange={(e) => setForgotField('username')(e.target.value)}
                    placeholder="ชื่อผู้ใช้"
                    autoFocus
                  />
                  <Input
                    value={forgotForm.totpToken}
                    onChange={(e) => setForgotField('totpToken')(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    style={{ textAlign: 'center', fontSize: 22, letterSpacing: 6 }}
                  />
                  <Input.Password
                    value={forgotForm.newPassword}
                    onChange={(e) => setForgotField('newPassword')(e.target.value)}
                    placeholder={`รหัสผ่านใหม่ (อย่างน้อย ${MIN_PASSWORD_LENGTH} ตัวอักษร)`}
                  />
                  <Input.Password
                    value={forgotForm.confirmPassword}
                    onChange={(e) => setForgotField('confirmPassword')(e.target.value)}
                    onPressEnter={onSubmitForgot}
                    placeholder="ยืนยันรหัสผ่านใหม่"
                  />
                </div>

                <Button
                  type="primary"
                  block
                  size="large"
                  className="mt-4"
                  loading={isResetting}
                  disabled={isResetting}
                  onClick={onSubmitForgot}
                >
                  ตั้งรหัสผ่านใหม่
                </Button>
                <Button type="link" block className="mt-2" onClick={backToLogin}>
                  ย้อนกลับไปหน้าเข้าสู่ระบบ
                </Button>
                <p className="text-xs text-gray-400 text-center mt-3">
                  เปิดแอปยืนยันตัวตนไม่ได้ หรือยังไม่เคยสแกน QR? ให้ผู้ดูแลระบบตั้งรหัสใหม่ให้
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Login);
